// Centralized marketing-page content. One object per public SEO page.
// Renderer: /app/frontend/src/pages/Marketing.jsx maps URL slug -> entry below.

const SOFTWARE_APPLICATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": "https://retainertracker.com/#software",
  name: "Retainer Tracker",
  alternateName: "RetainerTracker",
  url: "https://retainertracker.com/",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Retainer Tracker is a simple client retainer tracking app for consultants, studios, and small agencies. It helps users track retainer fees, included hours, logged time, delivery cost, profit, margin, and client status.",
  offers: [
    { "@type": "Offer", name: "Solo", price: "19", priceCurrency: "USD", url: "https://retainertracker.com/pricing" },
    { "@type": "Offer", name: "Studio", price: "49", priceCurrency: "USD", url: "https://retainertracker.com/pricing" },
    { "@type": "Offer", name: "Agency", price: "99", priceCurrency: "USD", url: "https://retainertracker.com/pricing" },
    { "@type": "Offer", name: "Scale", price: "179", priceCurrency: "USD", url: "https://retainertracker.com/pricing" },
  ],
};

const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

const breadcrumbSchema = (slug, title) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://retainertracker.com/" },
    { "@type": "ListItem", position: 2, name: title, item: `https://retainertracker.com${slug}` },
  ],
});

const COMMON_FAQS = [
  {
    q: "Is Retainer Tracker a CRM?",
    a: "No. Retainer Tracker is not a CRM. It does not manage leads, deals, or contact records. It helps you track retainer hours, cost, and profit for active clients.",
  },
  {
    q: "Is Retainer Tracker a time tracker?",
    a: "No. Retainer Tracker is not a timer. It helps you review retainer hours, delivery cost, profit, and status.",
  },
  {
    q: "Does Retainer Tracker have a free trial?",
    a: "Yes. Retainer Tracker has a 14-day free trial. You do not need a credit card to start.",
  },
];

export const PAGES = {
  "what-is-retainertracker": {
    title: "What is Retainer Tracker? | Simple Retainer Tracking App",
    description:
      "Retainer Tracker is a simple client retainer tracking app for consultants, studios, and small agencies. Track hours, cost, profit, margin, and client status.",
    h1: "What is Retainer Tracker?",
    intro: [
      "Retainer Tracker is a simple client retainer tracking app for consultants, studios, and small agencies.",
      "It helps you track retainer fees, included hours, logged time, delivery cost, gross profit, gross margin, and client status.",
      "Each client is marked as On Track, Close to Limit, or Losing Money.",
      "This helps you see which retainers are still making money and which ones need a closer look.",
    ],
    contextualLinks: [
      { to: "/client-retainer-tracker", label: "client retainer tracker" },
      { to: "/retainer-tracking-software", label: "retainer tracking software" },
      { to: "/help", label: "Help Center" },
    ],
    sections: [
      {
        h2: "Who Retainer Tracker is for",
        body: "Retainer Tracker is for service businesses that sell monthly retainers. It is a good fit for:",
        list: [
          "solo consultants",
          "fractional operators",
          "small agencies",
          "marketing studios",
          "design studios",
          "SEO consultants",
          "paid media consultants",
          "web studios",
          "boutique service firms",
        ],
      },
      {
        h2: "What Retainer Tracker tracks",
        list: [
          "monthly retainer fee",
          "included hours",
          "logged time",
          "internal hourly cost",
          "delivery cost",
          "gross profit",
          "gross margin",
          "remaining hours",
          "overage hours",
          "client status",
        ],
      },
      {
        h2: "Why retainer tracking matters",
        paragraphs: [
          "Retainers are easy to over-service.",
          "A client pays a fixed monthly fee.",
          "Then the work grows.",
          "Extra calls. Extra edits. Extra tasks. Extra quick asks.",
          "By the end of the month, the client may still look fine on paper.",
          "But the hours and cost may tell a different story.",
        ],
      },
      {
        h2: "What Retainer Tracker is not",
        body: "Retainer Tracker is not:",
        list: [
          "a CRM",
          "project management software",
          "a time tracker",
          "invoicing software",
          "PSA software",
          "enterprise software",
        ],
        outro: "It is a simple dashboard for retainer hours, cost, profit, and status.",
      },
    ],
    cta: { label: "Start free trial", url: "/dashboard", heading: "Start tracking your active retainers" },
    related: ["client-retainer-tracker", "retainer-tracking-software", "simple-client-tracking-app", "faqs"],
    screenshot: {
      src: "/screenshots/retainertracker-client-retainer-dashboard.png",
      filename: "retainertracker-client-retainer-dashboard.png",
      alt: "Retainer Tracker dashboard showing client retainers with included hours, logged time, delivery cost, profit, margin, and status.",
      caption: "Retainer Tracker shows hours, cost, profit, and status for each active retainer.",
    },
  },

  "simple-client-tracking-app": {
    title: "Simple Client Tracking App for Retainers | Retainer Tracker",
    description:
      "Looking for a simple client tracking app? Retainer Tracker helps consultants and small agencies track client retainers, hours, cost, profit, and status.",
    h1: "Simple client tracking app for retainers",
    intro: [
      "Retainer Tracker is a simple client tracking app for consultants, studios, and small agencies that work on monthly retainers.",
      "It is not a CRM.",
      "It does not manage sales pipelines, contacts, or tasks.",
      "It helps you track the numbers that matter for retainer clients:",
    ],
    contextualLinks: [
      { to: "/client-retainer-tracker", label: "client retainer tracker" },
      { to: "/retainer-tracking-spreadsheet-alternative", label: "retainer tracking spreadsheet alternative" },
    ],
    introList: ["included hours", "logged time", "delivery cost", "client profit", "margin", "status"],
    sections: [
      {
        h2: "What makes this client tracking app different?",
        paragraphs: [
          "Most client tracking apps focus on contacts, tasks, or sales.",
          "Retainer Tracker focuses on retainer health.",
          "It helps you see which clients are on track, close to the limit, or losing money.",
        ],
      },
      {
        h2: "Who should use it?",
        body: "Use Retainer Tracker if you manage monthly retainers and want to know:",
        list: [
          "which clients are over hours",
          "which clients cost too much to service",
          "which retainers still make money",
          "which accounts need a scope review",
        ],
      },
      {
        h2: "When a CRM is not enough",
        paragraphs: [
          "A CRM can show who your clients are.",
          "It may not show if a fixed-fee client is still profitable.",
          "Retainer Tracker helps with that.",
        ],
      },
      {
        h2: "Simple client tracking without a big system",
        paragraphs: [
          "Retainer Tracker is for people who want a focused dashboard.",
          "No project boards.",
          "No sales pipeline.",
          "No complex setup.",
          "Just retainer hours, cost, and profit.",
        ],
      },
    ],
    cta: { label: "Start free trial", url: "/dashboard", heading: "See your retainer health in one dashboard" },
    related: ["simple-customer-tracking-app", "client-retainer-tracker", "retainer-tracking-software", "retainertracker-vs-spreadsheets"],
  },

  "simple-customer-tracking-app": {
    title: "Simple Customer Tracking App for Retainers | Retainer Tracker",
    description:
      "Retainer Tracker is a simple customer tracking app for service businesses with monthly retainers. Track hours, delivery cost, profit, margin, and status.",
    h1: "Simple customer tracking app for retainer clients",
    intro: [
      "Retainer Tracker is a simple customer tracking app for service businesses that manage monthly retainers.",
      "It helps consultants, studios, and small agencies track each customer's retainer hours, delivery cost, profit, and status.",
      "It is built for ongoing service work, not sales pipelines.",
    ],
    sections: [
      {
        h2: "Track customer retainers, not sales leads",
        body: "Many customer tracking apps focus on leads, deals, and contact records. Retainer Tracker focuses on active retainer clients. Use it to see:",
        list: [
          "monthly retainer fee",
          "included hours",
          "logged time",
          "delivery cost",
          "profit",
          "margin",
          "status",
        ],
      },
      {
        h2: "See which customers cost too much to service",
        paragraphs: [
          "A customer can pay on time and still cost too much to service.",
          "Retainer Tracker helps you see when that happens.",
        ],
      },
      {
        h2: "Built for consultants and small agencies",
        paragraphs: [
          "Retainer Tracker is built for solo consultants, studios, and small agencies. It is not built for sales teams or enterprise support teams.",
        ],
      },
    ],
    cta: { label: "Start free trial", url: "/dashboard", heading: "Track retainer customers, not sales leads" },
    related: ["simple-client-tracking-app", "client-retainer-tracker", "retainer-profitability-dashboard", "faqs"],
  },

  "client-retainer-tracker": {
    title: "Client Retainer Tracker | Track Hours, Cost, and Profit",
    description:
      "A client retainer tracker helps consultants and small agencies track retainer fees, included hours, logged time, delivery cost, profit, and status.",
    h1: "Client retainer tracker",
    intro: [
      "A client retainer tracker helps you see how each monthly retainer is performing.",
      "Retainer Tracker shows retainer fees, included hours, logged time, delivery cost, profit, margin, and client status.",
      "Use it to see which clients are on track, close to the limit, or losing money.",
    ],
    contextualLinks: [
      { to: "/retainer-profitability-dashboard", label: "retainer profitability dashboard" },
      { to: "/retainer-tracking-spreadsheet-alternative", label: "retainer tracking spreadsheet alternative" },
      { to: "/help", label: "Help Center" },
    ],
    sections: [
      {
        h2: "What should a client retainer tracker include?",
        body: "Include:",
        list: [
          "client name",
          "monthly retainer fee",
          "included hours",
          "logged hours",
          "internal hourly cost",
          "delivery cost",
          "profit",
          "margin",
          "remaining hours",
          "overage hours",
          "status",
        ],
      },
      {
        h2: "Why spreadsheets get hard to manage",
        paragraphs: [
          "Spreadsheets work at first.",
          "Then the data gets old.",
          "Time is logged somewhere else.",
          "Costs are not checked.",
          "The founder has to remember which clients are over hours.",
          "Retainer Tracker gives you one dashboard instead.",
        ],
      },
      {
        h2: "How to know when a client is over hours",
        formula: "Utilization = logged hours ÷ included hours × 100",
        outro: "If a client has 20 included hours and 22 logged hours, utilization is 110%. That client is over hours.",
      },
      {
        h2: "How Retainer Tracker helps",
        paragraphs: [
          "Retainer Tracker calculates utilization, delivery cost, and margin for every client.",
          "Each client is marked On Track, Close to Limit, or Losing Money so you can act before month-end.",
        ],
      },
    ],
    cta: { label: "Try Retainer Tracker", url: "/dashboard", heading: "See every retainer in one place" },
    related: ["how-to-track-retainer-hours", "retainer-profitability-dashboard", "retainer-tracking-spreadsheet-alternative", "retainer-tracking-software"],
    screenshot: {
      src: "/screenshots/retainertracker-retainer-status-table.png",
      filename: "retainertracker-retainer-status-table.png",
      alt: "Retainer Tracker table showing client retainers marked On Track, Close to Limit, and Losing Money.",
      caption: "Each client gets a clear status: On Track, Close to Limit, or Losing Money.",
    },
  },

  "retainer-tracking-software": {
    title: "Retainer Tracking Software for Consultants and Small Agencies",
    description:
      "Retainer tracking software for consultants, studios, and small agencies. Track included hours, logged time, delivery cost, profit, margin, and status.",
    h1: "Retainer tracking software",
    intro: [
      "Retainer Tracker is retainer tracking software for consultants, studios, and small agencies.",
      "It helps you track hours, cost, and profit for every retainer.",
      "Use it to see which clients are on track, close to the limit, or losing money.",
    ],
    contextualLinks: [
      { to: "/client-retainer-tracker", label: "client retainer tracker" },
      { to: "/retainer-tracking-spreadsheet-alternative", label: "retainer tracking spreadsheet alternative" },
    ],
    sections: [
      {
        h2: "What is retainer tracking software?",
        paragraphs: [
          "Retainer tracking software helps service businesses see how each monthly retainer is performing.",
          "It tracks included hours, logged time, delivery cost, profit, margin, and status in one place.",
        ],
      },
      {
        h2: "Who needs retainer tracking software?",
        list: [
          "solo consultants",
          "fractional operators",
          "small agencies",
          "marketing studios",
          "design studios",
          "SEO and paid media consultants",
        ],
      },
      {
        h2: "Retainer tracking software vs time tracking software",
        paragraphs: [
          "Time tracking software shows where time went.",
          "Retainer tracking software shows if the retainer is still making money.",
        ],
      },
      {
        h2: "Retainer tracking software vs project management software",
        paragraphs: [
          "Project management software helps teams manage work.",
          "Retainer tracking software helps you review hours, delivery cost, profit, and margin.",
        ],
      },
      {
        h2: "Retainer tracking software vs spreadsheets",
        paragraphs: [
          "Spreadsheets are fine when you have a few clients and update them often.",
          "Retainer tracking software is better when you want one dashboard for retainer hours, cost, and profit.",
        ],
      },
    ],
    cta: { label: "Start free trial", url: "/dashboard", heading: "Start with your active retainers" },
    related: ["client-retainer-tracker", "retainer-profitability-dashboard", "retainertracker-vs-time-trackers", "retainertracker-vs-project-management-software"],
  },

  "retainer-tracking-spreadsheet-alternative": {
    title: "Retainer Tracking Spreadsheet Alternative | Retainer Tracker",
    description:
      "Replace your retainer tracking spreadsheet with a simple dashboard for included hours, logged time, delivery cost, profit, margin, and client status.",
    h1: "Retainer tracking spreadsheet alternative",
    intro: [
      "A retainer tracking spreadsheet can work for a while.",
      "But it can become hard to keep up to date.",
      "Retainer Tracker gives you a simple dashboard for retainer hours, cost, profit, margin, and status.",
    ],
    contextualLinks: [
      { to: "/client-retainer-tracker", label: "client retainer tracker" },
      { to: "/retainer-profitability-dashboard", label: "retainer profitability dashboard" },
    ],
    sections: [
      {
        h2: "Why people use spreadsheets for retainers",
        paragraphs: [
          "Spreadsheets are free, familiar, and flexible.",
          "Most retainer trackers start as one tab in a sheet a founder built one weekend.",
        ],
      },
      {
        h2: "Where retainer spreadsheets break",
        list: [
          "the data goes stale between months",
          "time is logged in another tool",
          "delivery cost is calculated by hand",
          "client status is only in the founder's head",
          "sharing it with the team creates more versions, not less",
        ],
      },
      {
        h2: "What Retainer Tracker shows instead",
        paragraphs: [
          "Retainer Tracker shows every retainer on one screen with hours, cost, profit, margin, and status.",
          "You see which clients are over hours and which clients cost too much to service before the month is over.",
        ],
      },
      {
        h2: "Spreadsheet vs Retainer Tracker",
        table: {
          head: ["Task", "Spreadsheet", "Retainer Tracker"],
          rows: [
            ["Track included hours", "Manual", "Built into each retainer"],
            ["Track logged time", "Manual", "Dashboard view"],
            ["Calculate delivery cost", "Formula needed", "Calculated for you"],
            ["See client status", "Manual check", "On Track, Close to Limit, Losing Money"],
            ["Review monthly profit", "Manual", "Dashboard summary"],
          ],
        },
      },
      {
        h2: "When to move from a spreadsheet to Retainer Tracker",
        paragraphs: [
          "When you have more than 3 active retainers, when more than one person needs to see the numbers, or when you stop trusting your own sheet — it is time.",
        ],
      },
    ],
    cta: { label: "Replace your spreadsheet", url: "/dashboard", heading: "Move from a spreadsheet to a real retainer dashboard" },
    related: ["client-retainer-tracker", "retainer-tracking-software", "retainer-profitability-dashboard", "retainertracker-vs-spreadsheets"],
  },

  "retainer-profitability-dashboard": {
    title: "Retainer Profitability Dashboard | Track Client Profit",
    description:
      "Track retainer profit, delivery cost, gross margin, logged time, and client status in one simple dashboard.",
    h1: "Retainer profitability dashboard",
    intro: [
      "A retainer profitability dashboard helps you see which clients are still making money.",
      "Retainer Tracker shows monthly fee, logged time, delivery cost, gross profit, gross margin, and client status.",
    ],
    contextualLinks: [
      { to: "/client-retainer-tracker", label: "client retainer tracker" },
      { to: "/help/calculate-gross-margin", label: "how gross margin is calculated" },
      { to: "/help", label: "Help Center" },
    ],
    sections: [
      {
        h2: "Why retainer profit is easy to miss",
        paragraphs: [
          "A client can pay on time and still be a money-losing account.",
          "The work grows. The fee stays the same. The hours and cost quietly creep up.",
          "A retainer profitability dashboard makes that visible.",
        ],
      },
      {
        h2: "How to calculate delivery cost",
        formula: "Delivery cost = logged hours × internal hourly cost",
      },
      {
        h2: "How to calculate gross profit",
        formula: "Gross profit = monthly retainer fee − delivery cost",
      },
      {
        h2: "How to calculate gross margin",
        formula: "Gross margin = gross profit ÷ monthly retainer fee × 100",
      },
      {
        h2: "See which clients need a closer look",
        paragraphs: [
          "Retainer Tracker marks every client On Track, Close to Limit, or Losing Money so you can act before month-end.",
        ],
      },
    ],
    cta: { label: "Review your retainers", url: "/dashboard", heading: "See which clients are still making money" },
    related: ["client-retainer-tracker", "how-to-track-retainer-hours", "retainer-tracking-software", "retainertracker-vs-time-trackers"],
    screenshot: {
      src: "/screenshots/retainertracker-retainer-profit-dashboard.png",
      filename: "retainertracker-retainer-profit-dashboard.png",
      alt: "Retainer Tracker dashboard showing retainer profit, gross margin, delivery cost, and client status.",
      caption: "Profit, margin, delivery cost, and status — all in one view.",
    },
  },

  "how-to-track-retainer-hours": {
    title: "How to Track Retainer Hours | Retainer Tracker",
    description:
      "Learn how to track retainer hours with included hours, logged time, utilization, remaining hours, and overage hours.",
    h1: "How to track retainer hours",
    intro: [
      "Tracking retainer hours is the first step to knowing if a retainer is still profitable.",
      "You need three numbers per client: included hours, logged hours, and internal hourly cost.",
    ],
    sections: [
      {
        h2: "Step 1: Set included hours per retainer",
        paragraphs: ["This is what the client pays for each month. Write it down. Do not guess."],
      },
      {
        h2: "Step 2: Log time against each retainer",
        paragraphs: [
          "You can log time inside Retainer Tracker, or import logged hours from your time tracker.",
          "What matters is that the hours are in one place by client.",
        ],
      },
      {
        h2: "Step 3: Calculate utilization",
        formula: "Utilization = logged hours ÷ included hours × 100",
        outro: "If utilization is over 100%, the client is over hours.",
      },
      {
        h2: "Step 4: Check remaining and overage hours",
        list: [
          "Remaining hours = included hours − logged hours (if positive)",
          "Overage hours = logged hours − included hours (if positive)",
        ],
      },
      {
        h2: "Step 5: Review before month-end",
        paragraphs: [
          "The best time to act is before the month closes. A scope review or price conversation is easier when you have the numbers.",
        ],
      },
    ],
    cta: { label: "Track retainer hours", url: "/dashboard", heading: "Start tracking your retainer hours" },
    related: ["client-retainer-tracker", "retainer-profitability-dashboard", "retainer-tracking-software", "faqs"],
  },

  "why-retainertracker": {
    title: "Why Retainer Tracker Exists | Founder Story",
    description:
      "Retainer Tracker was built to help consultants, studios, and small agencies see when retainers are over hours, close to the limit, or losing money.",
    h1: "Why Retainer Tracker exists",
    intro: [
      "Retainer Tracker was built for a simple reason.",
      "Retainers are easy to over-service.",
      "A client pays a fixed monthly fee.",
      "The work starts out clear.",
      "Then the small things pile up.",
      "Extra calls. Extra edits. Extra tasks. Extra quick asks.",
      "By the end of the month, the account may still look fine.",
      "But the hours and delivery cost may tell a different story.",
      "Retainer Tracker exists to make that easier to see.",
    ],
    sections: [
      {
        h2: "Why spreadsheets are not always enough",
        paragraphs: [
          "Most agency owners start with a spreadsheet.",
          "It works until the team grows, the data goes stale, and no one is sure which version is current.",
        ],
      },
      {
        h2: "Why Retainer Tracker is simple on purpose",
        paragraphs: [
          "We do not try to be a CRM, project tool, or time tracker.",
          "We try to answer one question well: which retainers are still worth the work?",
        ],
      },
      {
        h2: "Who Retainer Tracker is built for",
        list: ["solo consultants", "fractional operators", "small agencies", "studios", "boutique service firms"],
      },
      {
        h2: "What Retainer Tracker will not become",
        list: ["a CRM", "a sales pipeline", "a project board", "a Gantt chart", "a timer"],
        outro: "Retainer Tracker stays focused on retainer health.",
      },
    ],
    cta: { label: "Start free trial", url: "/dashboard", heading: "Use the simple retainer dashboard" },
    related: ["what-is-retainertracker", "client-retainer-tracker", "retainer-tracking-software", "faqs"],
  },

  "retainertracker-vs-spreadsheets": {
    title: "Retainer Tracker vs Spreadsheets",
    description:
      "Spreadsheets work for a few clients. Retainer Tracker gives you one dashboard for retainer hours, cost, profit, and status.",
    h1: "Retainer Tracker vs spreadsheets",
    intro: [
      "Spreadsheets are fine when you have a few clients and update them often.",
      "Retainer Tracker is better when you want one dashboard for retainer hours, cost, profit, and status.",
    ],
    sections: [
      {
        h2: "What spreadsheets are good at",
        list: ["one-off lists", "simple math", "personal trackers"],
      },
      {
        h2: "Where spreadsheets fall short for retainers",
        list: [
          "stale data between months",
          "manual delivery cost math",
          "no client status labels",
          "no shared, up-to-date view",
        ],
      },
      {
        h2: "What Retainer Tracker gives you instead",
        list: [
          "one dashboard for every active retainer",
          "automatic delivery cost and margin math",
          "On Track, Close to Limit, and Losing Money labels",
          "monthly snapshots so you can see trends",
        ],
      },
    ],
    cta: { label: "Replace your retainer spreadsheet", url: "/dashboard", heading: "Move from a sheet to a dashboard" },
    related: ["retainer-tracking-spreadsheet-alternative", "client-retainer-tracker", "retainer-tracking-software", "retainer-profitability-dashboard"],
  },

  "retainertracker-vs-time-trackers": {
    title: "Retainer Tracker vs Time Trackers",
    description:
      "Time trackers show where time went. Retainer Tracker shows if the retainer is still making money.",
    h1: "Retainer Tracker vs time trackers",
    intro: [
      "Time trackers show where time went.",
      "Retainer Tracker shows if the retainer is still making money.",
    ],
    sections: [
      {
        h2: "What time trackers do well",
        list: ["start/stop timers", "timesheets by user", "billable vs non-billable splits"],
      },
      {
        h2: "What time trackers do not do",
        list: [
          "calculate retainer-level profit",
          "label clients by status",
          "show which retainers cost too much to service",
        ],
      },
      {
        h2: "How to use both",
        paragraphs: [
          "Log hours in your time tracker. Use Retainer Tracker to roll those hours up to retainer profit, margin, and status.",
        ],
      },
    ],
    cta: { label: "Track retainer profit", url: "/dashboard", heading: "From time to retainer profit" },
    related: ["retainer-profitability-dashboard", "client-retainer-tracker", "retainer-tracking-software", "how-to-track-retainer-hours"],
  },

  "retainertracker-vs-project-management-software": {
    title: "Retainer Tracker vs Project Management Software",
    description:
      "Project management software helps teams manage work. Retainer Tracker helps you review retainer hours, delivery cost, profit, margin, and status.",
    h1: "Retainer Tracker vs project management software",
    intro: [
      "Project management software helps teams manage work.",
      "Retainer Tracker helps you review retainer hours, delivery cost, profit, margin, and status.",
    ],
    sections: [
      {
        h2: "What project tools focus on",
        list: ["tasks", "boards", "due dates", "team workload"],
      },
      {
        h2: "What Retainer Tracker focuses on",
        list: [
          "retainer fees",
          "included hours",
          "logged time",
          "delivery cost",
          "profit and margin",
          "client status",
        ],
      },
      {
        h2: "Use them together",
        paragraphs: [
          "Use your project tool to run the work. Use Retainer Tracker to review if the retainer is still worth running.",
        ],
      },
    ],
    cta: { label: "Start tracking retainers", url: "/dashboard", heading: "A dashboard for retainer health" },
    related: ["client-retainer-tracker", "retainer-tracking-software", "retainer-profitability-dashboard", "retainertracker-vs-psa-software"],
  },

  "retainertracker-vs-psa-software": {
    title: "Retainer Tracker vs PSA Software",
    description:
      "PSA software can be useful for larger teams. Retainer Tracker is for consultants, studios, and small agencies that want a simple retainer dashboard.",
    h1: "Retainer Tracker vs PSA software",
    intro: [
      "PSA software (Professional Services Automation) can be useful for larger teams.",
      "Retainer Tracker is for consultants, studios, and small agencies that want a simple retainer dashboard.",
    ],
    sections: [
      {
        h2: "What PSA tools do",
        list: ["resource planning", "complex billing", "approval workflows", "enterprise reporting"],
      },
      {
        h2: "What Retainer Tracker does",
        list: [
          "tracks retainer hours, cost, and profit",
          "labels clients On Track, Close to Limit, or Losing Money",
          "stays simple for small teams",
        ],
      },
      {
        h2: "Who Retainer Tracker is for instead",
        list: ["solo consultants", "fractional operators", "small agencies", "studios", "boutique service firms"],
      },
    ],
    cta: { label: "Use a simpler retainer tracker", url: "/dashboard", heading: "A simple alternative to PSA software" },
    related: ["client-retainer-tracker", "retainer-tracking-software", "retainertracker-vs-spreadsheets", "what-is-retainertracker"],
  },

  faqs: {
    title: "Retainer Tracker FAQs | Retainer Tracking Software",
    description:
      "Answers to common questions about Retainer Tracker, retainer tracking, client profit, included hours, delivery cost, margin, and retainer status.",
    h1: "Retainer Tracker FAQs",
    intro: ["Common questions about Retainer Tracker."],
    faqs: [
      {
        q: "What is Retainer Tracker?",
        a: "Retainer Tracker is a simple client retainer tracking app for consultants, studios, and small agencies. It helps users track retainer fees, included hours, logged time, delivery cost, profit, margin, and client status.",
      },
      {
        q: "Is Retainer Tracker a CRM?",
        a: "No. Retainer Tracker is not a CRM. It does not manage leads, deals, or contact records. It helps you track retainer hours, cost, and profit for active clients.",
      },
      {
        q: "Is Retainer Tracker a time tracker?",
        a: "No. Retainer Tracker is not a timer. It helps you review retainer hours, delivery cost, profit, and status.",
      },
      {
        q: "Who is Retainer Tracker for?",
        a: "Retainer Tracker is for solo consultants, fractional operators, studios, and small agencies that manage monthly retainers.",
      },
      {
        q: "What does Retainer Tracker track?",
        a: "It tracks monthly retainer fee, included hours, logged time, internal hourly cost, delivery cost, profit, margin, remaining hours, overage hours, and status.",
      },
      {
        q: "What does On Track mean?",
        a: "On Track means the client is within the expected hour usage and margin range.",
      },
      {
        q: "What does Close to Limit mean?",
        a: "Close to Limit means the client is close to the included hours or margin is getting low.",
      },
      {
        q: "What does Losing Money mean?",
        a: "Losing Money means the client may cost too much to service.",
      },
      {
        q: "How is Retainer Tracker priced?",
        a: "Plans are based on the number of active retainers tracked, not seats.",
      },
      {
        q: "Does Retainer Tracker have a free trial?",
        a: "Yes. Retainer Tracker has a 14-day free trial. No credit card required.",
      },
    ],
    cta: { label: "Start free trial", url: "/dashboard", heading: "Try Retainer Tracker free for 14 days" },
    related: ["what-is-retainertracker", "client-retainer-tracker", "retainer-tracking-software", "why-retainertracker"],
  },

  help: {
    title: "Retainer Tracker Help Center",
    description:
      "Learn how Retainer Tracker tracks retainer hours, delivery cost, profit, margin, and client status.",
    h1: "Retainer Tracker Help Center",
    intro: [
      "Learn how Retainer Tracker tracks retainer hours, delivery cost, profit, margin, and client status.",
      "Choose a topic below or visit our FAQs.",
    ],
    sections: [
      {
        h2: "Getting started",
        list: [
          "Add a retainer: open the dashboard, click Add Retainer, fill in monthly fee, included hours, and internal hourly cost.",
          "Set your workspace defaults in Settings so new retainers start with the right hourly cost and margin target.",
        ],
      },
      {
        h2: "Status labels",
        list: [
          "On Track: utilization ≤ 85% and margin ≥ 35%.",
          "Close to Limit: utilization between 86% and 100% OR margin between 15% and 35%.",
          "Losing Money: utilization > 100% OR margin < 15% OR delivery cost exceeds the monthly fee.",
        ],
      },
      {
        h2: "Formulas we use",
        list: [
          "Utilization = logged hours ÷ included hours × 100",
          "Delivery cost = logged hours × internal hourly cost",
          "Gross profit = monthly retainer fee − delivery cost",
          "Gross margin = gross profit ÷ monthly retainer fee × 100",
          "Remaining hours = included hours − logged hours",
          "Overage hours = logged hours − included hours",
        ],
      },
      {
        h2: "Pricing and active retainers",
        paragraphs: [
          "Plans are based on the number of active retainers, not seats. Sample retainers do not count against your cap.",
          "You can upgrade or downgrade any time from Settings → Billing.",
        ],
      },
      {
        h2: "Free trial",
        paragraphs: [
          "Every account gets a 14-day free trial. No credit card required. Your data stays safe if the trial ends — pick a plan to keep editing.",
        ],
      },
    ],
    cta: { label: "Open my dashboard", url: "/dashboard", heading: "Need a hand? Email support@retainertracker.com" },
    related: ["faqs", "what-is-retainertracker", "client-retainer-tracker", "retainer-profitability-dashboard"],
  },
};

// Inject a FAQPage schema entry only when the page actually has FAQs.
export const pageStructuredData = (slug) => {
  const page = PAGES[slug];
  if (!page) return [];
  const blocks = [SOFTWARE_APPLICATION_SCHEMA, breadcrumbSchema(`/${slug}`, page.h1)];
  if (page.faqs?.length) blocks.push(faqSchema(page.faqs));
  return blocks;
};

// ---------------- Help subpages ----------------
// Slug includes "help/" so the route resolves to /help/<topic>.
const helpPage = ({ slug, title, h1, intro, sections, related, screenshot }) => ({
  title: `${title} | Retainer Tracker Help`,
  description: intro[0],
  h1,
  intro,
  sections,
  cta: { label: "Open my dashboard", url: "/dashboard", heading: "Need a hand? Email support@retainertracker.com" },
  related: related || ["help", "faqs", "client-retainer-tracker", "retainer-profitability-dashboard"],
  ...(screenshot ? { screenshot } : {}),
  _slugOverride: slug,
});

Object.assign(PAGES, {
  "help/add-a-retainer": helpPage({
    slug: "help/add-a-retainer",
    title: "Add a retainer",
    h1: "How to add a retainer",
    intro: [
      "Adding a retainer takes about a minute.",
      "You need the client name, monthly fee, included hours, and your internal hourly cost.",
    ],
    sections: [
      {
        h2: "Step by step",
        list: [
          "Open the dashboard and click Add Retainer.",
          "Enter the client name.",
          "Enter the monthly retainer fee (in your default currency).",
          "Enter the included hours per month.",
          "Enter your internal hourly cost. This is what the work costs you, not what the client pays.",
          "(Optional) Add a service type to group similar clients later.",
          "Save the retainer.",
        ],
      },
      {
        h2: "After you add a retainer",
        paragraphs: [
          "Log time against the retainer as the month goes on.",
          "The dashboard will show hours used, hours left, earned to date, profit, margin, and a status label.",
        ],
      },
    ],
    screenshot: {
      src: "/screenshots/retainertracker-add-client-retainer.png",
      filename: "retainertracker-add-client-retainer.png",
      alt: "Retainer Tracker form for adding a client retainer with monthly fee, included hours, logged time, and internal hourly cost.",
      caption: "The Add Retainer form takes about a minute to fill in.",
    },
  }),  "help/status-labels": helpPage({
    slug: "help/status-labels",
    title: "Status labels",
    h1: "What the status labels mean",
    intro: [
      "Every retainer gets one of three status labels.",
      "The label changes as you log more hours.",
    ],
    sections: [
      {
        h2: "On Track",
        paragraphs: [
          "Utilization is at or below 85% AND margin is at or above 35%.",
          "The client is using hours at a healthy pace and the retainer is profitable.",
        ],
      },
      {
        h2: "Close to Limit",
        paragraphs: [
          "Utilization is between 86% and 100% OR margin is between 15% and 35%.",
          "You are still within scope, but it is worth checking before month-end.",
        ],
      },
      {
        h2: "Losing Money",
        paragraphs: [
          "Utilization is over 100% OR margin is below 15% OR delivery cost exceeds the monthly fee.",
          "Time to consider a scope review or a price change.",
        ],
      },
    ],
  }),
  "help/calculate-utilization": helpPage({
    slug: "help/calculate-utilization",
    title: "Utilization",
    h1: "How utilization is calculated",
    intro: [
      "Utilization shows how much of the included hours a client has used.",
    ],
    sections: [
      { h2: "Formula", formula: "Utilization = logged hours ÷ included hours × 100" },
      {
        h2: "Example",
        paragraphs: [
          "If a client has 20 included hours and 15 logged hours, utilization is 75%.",
          "If a client has 20 included hours and 22 logged hours, utilization is 110%. They are over hours.",
        ],
      },
    ],
  }),
  "help/calculate-delivery-cost": helpPage({
    slug: "help/calculate-delivery-cost",
    title: "Delivery cost",
    h1: "How delivery cost is calculated",
    intro: [
      "Delivery cost is what it actually costs you to service the retainer this month.",
      "It is your team's time at your internal hourly cost — not what the client pays.",
    ],
    sections: [
      { h2: "Formula", formula: "Delivery cost = logged hours × internal hourly cost" },
      {
        h2: "Example",
        paragraphs: [
          "Logged hours: 15. Internal hourly cost: $80/h.",
          "Delivery cost = 15 × 80 = $1,200.",
        ],
      },
    ],
  }),
  "help/calculate-gross-profit": helpPage({
    slug: "help/calculate-gross-profit",
    title: "Gross profit",
    h1: "How gross profit is calculated",
    intro: ["Gross profit is what's left after delivery cost is taken off the retainer fee."],
    sections: [
      { h2: "Formula", formula: "Gross profit = monthly retainer fee − delivery cost" },
      {
        h2: "Example",
        paragraphs: [
          "Monthly fee: $3,000. Delivery cost: $1,200.",
          "Gross profit = 3,000 − 1,200 = $1,800.",
        ],
      },
    ],
  }),
  "help/calculate-gross-margin": helpPage({
    slug: "help/calculate-gross-margin",
    title: "Gross margin",
    h1: "How gross margin is calculated",
    intro: ["Gross margin is gross profit as a percentage of the monthly retainer fee."],
    sections: [
      { h2: "Formula", formula: "Gross margin = gross profit ÷ monthly retainer fee × 100" },
      {
        h2: "Example",
        paragraphs: [
          "Gross profit: $1,800. Monthly fee: $3,000.",
          "Gross margin = 1,800 ÷ 3,000 × 100 = 60%.",
        ],
      },
      {
        h2: "What's a healthy margin?",
        paragraphs: [
          "Retainer Tracker treats 35% or higher as On Track and below 15% as Losing Money.",
          "Adjust your hourly cost and fee to keep margins where you need them.",
        ],
      },
    ],
  }),
  "help/remaining-hours-and-overage-hours": helpPage({
    slug: "help/remaining-hours-and-overage-hours",
    title: "Remaining and overage hours",
    h1: "Remaining hours and overage hours",
    intro: [
      "Remaining hours show how many included hours are left this month.",
      "Overage hours show how many hours you've worked past the included hours.",
    ],
    sections: [
      {
        h2: "Formulas",
        list: [
          "Remaining hours = included hours − logged hours (if positive)",
          "Overage hours = logged hours − included hours (if positive)",
        ],
      },
      {
        h2: "Example",
        paragraphs: [
          "Included hours: 20. Logged hours: 15. Remaining = 5h. Overage = 0h.",
          "Included hours: 20. Logged hours: 22. Remaining = 0h. Overage = 2h.",
        ],
      },
    ],
  }),
  "help/pricing-and-active-retainers": helpPage({
    slug: "help/pricing-and-active-retainers",
    title: "Pricing and active retainers",
    h1: "How pricing works",
    intro: [
      "Retainer Tracker plans are based on the number of active retainers you track, not seats.",
    ],
    sections: [
      {
        h2: "Plan caps",
        list: [
          "Solo — up to 5 active retainers",
          "Studio — up to 15 active retainers",
          "Agency — up to 40 active retainers",
          "Scale — unlimited",
        ],
      },
      {
        h2: "What counts as active",
        paragraphs: [
          "Any retainer you create counts toward your plan cap, except sample retainers, which are free.",
          "Sample retainers go away as soon as you create your first real retainer.",
        ],
      },
      {
        h2: "Switching plans",
        paragraphs: [
          "Open Settings → Billing → Manage billing to upgrade, downgrade, or cancel at any time.",
        ],
      },
    ],
  }),
  "help/free-trial": helpPage({
    slug: "help/free-trial",
    title: "Free trial",
    h1: "How the free trial works",
    intro: [
      "Every new account gets a 14-day free trial.",
      "No credit card is required to start.",
    ],
    sections: [
      {
        h2: "What you get during the trial",
        list: [
          "Full access to the dashboard.",
          "Add and edit retainers.",
          "Log hours, take snapshots, export CSVs.",
        ],
      },
      {
        h2: "When the trial ends",
        paragraphs: [
          "Pick a plan to keep editing your retainers.",
          "Your data stays safe — nothing is deleted if your trial expires.",
        ],
      },
    ],
  }),
});

export { COMMON_FAQS, SOFTWARE_APPLICATION_SCHEMA, faqSchema, breadcrumbSchema };
